import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Chip,
  GridLegacy as Grid,
  Paper,
  Typography,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  ListSubheader,
  IconButton,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { FixedSizeList } from 'react-window';
import BorderBox from '../../BorderBox';
import { equipmentPromotionStatus, hasUnpublishedChanges } from '../util/equipment';

function PromotionChip({ promotedEquipmentById, equipment }) {
  const promotionState = equipmentPromotionStatus(promotedEquipmentById, equipment);
  if (promotionState.status === 'unpublished') {
    return (
      <Grid item>
        <Chip label="Not Published" color="textSecondary" />
      </Grid>
    );
  }
  if (promotionState.status === 'published') {
    return (
      <Grid item>
        <Chip label="Published" color="success" />
      </Grid>
    );
  }
  return (
    <Grid item>
      <Chip label="Partially Published" color="warning" />
    </Grid>
  );
}

function ChangedChip({ equipment }) {
  if (hasUnpublishedChanges(equipment)) {
    return (
      <Grid item>
        <Chip label="Unpublished Changes" color="warning" />
      </Grid>
    );
  }
  return null;
}

const EquipmentPointsCard = ({
  equipment,
  pointTypes,
  equipTypes,
  onDeleteEquipment,
  onAddPoint,
  onUpdateEquipment,
  promotedEquipmentById,
}) => {
  const pointsHeight = useMemo(
    () => Math.min(225, Math.max(100, (equipment.points?.length ?? 0) * 32)),
    [equipment.points]
  );

  function renderRow(props) {
    const { index, style } = props;
    const point = equipment.points[index];

    return (
      <ListItem
        style={style}
        key={index}
        component="div"
        disablePadding
        // secondaryAction={
        //   <Grid container>
        //     <Button
        //       variant="outlined"
        //       color="error"
        //       sx={{ height: '28px' }}
        //       onClick={() => onRemovePoint(equipment, point)}
        //     >
        //       Remove
        //     </Button>
        //   </Grid>
        // }
      >
        <ListItemButton sx={{ height: '32px' }}>
          <Grid container columnSpacing={4}>
            <Grid item xs={3}>
              <ListItemText
                primary={point.properties?.objectIdentifier}
                primaryTypographyProps={{
                  style: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <ListItemText
                primary={point.properties?.objectName}
                primaryTypographyProps={{
                  style: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <ListItemText
                primary={pointTypes[point.point_type_id]?.name}
                primaryTypographyProps={{
                  style: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </Grid>
          </Grid>
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <Grid item>
      <Paper elevation={3}>
        <Grid container rowSpacing={1} direction="column" alignItems="stretch" pr={2} pl={2} pb={1}>
          <Grid item container direction="row" columnSpacing={2}>
            <Grid item>
              <Typography gutterBottom variant="h5" component="div">
                {equipment.name}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={() => onUpdateEquipment(equipment)} size="small">
                <Edit />
              </IconButton>
            </Grid>
            <Grid item /> {/* Just a spacing Element */}
            <PromotionChip promotedEquipmentById={promotedEquipmentById} equipment={equipment} />
            <ChangedChip equipment={equipment} />
          </Grid>
          <Grid item container>
            <Grid item container xs={4} direction="row" alignItems="center" alignContent="start">
              <Grid item container pr={2} xs={5} justifyContent="flex-end">
                <Typography variant="subtitle2" fontWeight={500} fontSize="1.1rem">
                  Equipment Type
                </Typography>
              </Grid>
              <Grid item xs={7}>
                {equipTypes[equipment.equipment_type_id]?.tagName}
              </Grid>
            </Grid>

            <Grid item xs>
              <BorderBox title="Points">
                <ListSubheader sx={{ height: '32px' }}>
                  <Grid container display="row">
                    <Grid item xs={3}>
                      Identifier
                    </Grid>
                    <Grid item xs={4}>
                      Name
                    </Grid>
                    <Grid item xs={5}>
                      Type
                    </Grid>
                  </Grid>
                </ListSubheader>
                <FixedSizeList
                  height={pointsHeight}
                  itemSize={32}
                  itemCount={equipment.points?.length || 0}
                  overscanCount={5}
                >
                  {renderRow}
                </FixedSizeList>
              </BorderBox>
            </Grid>
          </Grid>
          <Grid item container direction="row" justifyContent="flex-end" columnSpacing={2}>
            <Grid item>
              <Button
                color="info"
                variant="contained"
                sx={{ wordWrap: 'break-word' }}
                onClick={() => onAddPoint(equipment)}
              >
                Edit Points
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="error"
                variant="outlined"
                sx={{ wordWrap: 'break-word' }}
                disabled={equipment.points?.length > 0 || equipment.promoted_equipment_id != null}
                onClick={() => onDeleteEquipment(equipment)}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

const mapStateToProps = (state, { equipment }) => ({
  equipment,
  pointTypes: state.entities.pointTypes.byId,
  promotedEquipmentById: state.entities.equipment.byId ?? {},
  equipTypes: state.entities.equipTypes.byId,
});

export default connect(mapStateToProps)(EquipmentPointsCard);
